import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PasswordResetRequest: React.FC = () => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    const email = localStorage.getItem('resetEmail');
    if (!email) {
      navigate('/forgot-password');
      return;
    }

    try {
      setError(null);
      setLoading(true);
      const { error } = await resetPassword(email);
      
      if (error) {
        throw error;
      }
      
      toast.success(t('resetLinkSent'));
    } catch (error: any) {
      setError(error.message || t('resetLinkFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <Logo size="large" className="mx-auto mb-6" />
        
        <div className="flex justify-center mb-6">
          <Mail className="h-16 w-16 text-blue-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {t('checkYourEmail')}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {t('passwordResetInstructions')}
        </p>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <Link
            to="/login"
            className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            {t('backToLogin')}
          </Link>
          
          <p className="text-sm text-gray-500">
            {t('didntReceiveEmail')}{' '}
            <button
              onClick={handleResendEmail}
              disabled={loading}
              className="text-blue-600 hover:text-blue-800 focus:outline-none disabled:opacity-50"
            >
              {loading ? t('sending') : t('resendEmail')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequest;